import { Outlet, createRootRouteWithContext, createRoute, type RouteComponent } from '@tanstack/react-router'

import { useTranslate } from '@/shared/i18n/I18nProvider'
import { UnsavedIntentProvider } from '@/shared/routing/unsavedIntents'
import { FormFactorProvider } from '@/shared/workspace/formFactor'
import { BuildUpdateWatcher } from './BuildUpdateWatcher'
import { DocumentTitle } from './documentTitle'
import { ErrorPanel } from './ErrorPanel'
import { LOGIN_ROUTE } from '@/shared/routing/returnTarget'
import { externalAuthorizationSearch } from '@/shared/routing/externalAuthorization'
import { designProposalSearch } from '@/shared/routing/designProposalSearch'
import { requireAccountConnectors, requireAccountDesignDrafts, requireApplication, requireOrganizationSelection,
  requirePasswordChange, type RouterContext } from './routeAccess'

export function RootLayout() {
  const t = useTranslate()
  return (
    <>
      <a className="skip-link" href="#main">{t('app.skipToContent')}</a>
      <DocumentTitle />
      {/* Intents outlive the authenticated shell; the watcher shares its register. */}
      <UnsavedIntentProvider>
        <BuildUpdateWatcher />
        <Outlet />
      </UnsavedIntentProvider>
    </>
  )
}

// The router alone imports pages; this owner composes their common session boundary.
export function createSessionRoutes(screens: {
  login: RouteComponent
  notFound: RouteComponent
  publicDocument: RouteComponent
  invitation: RouteComponent
  passwordChange: RouteComponent
  connectors: RouteComponent
  integrations: RouteComponent
  designIntegrations: RouteComponent
  designDrafts: RouteComponent
  designDraft: RouteComponent
  organizations: RouteComponent
  organizationCreate: RouteComponent
  shell: RouteComponent
}) {
  const rootRoute = createRootRouteWithContext<RouterContext>()({
    component: RootLayout,
    notFoundComponent: screens.notFound,
    staticData: { title: null },
  })
  const loginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: LOGIN_ROUTE,
    component: screens.login,
    staticData: { title: null },
    validateSearch: (search: Record<string, unknown>): { telegram?: string; authorize?: string } => ({
      ...externalAuthorizationSearch(search),
      ...(typeof search['telegram'] === 'string' ? { telegram: search['telegram'] } : {}),
    }),
  })
  const organizationSelectionRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/organizations',
    component: screens.organizations,
    staticData: { title: 'organizations.choose' },
    // `redirect` is where the workspace was going when it stopped to ask for a
    // workplace; it is reduced to an internal path where it is followed.
    validateSearch: (search: Record<string, unknown>): { eventId?: string; redirect?: string; authorize?: string } => ({
      ...externalAuthorizationSearch(search),
      ...(typeof search['eventId'] === 'string' ? { eventId: search['eventId'] } : {}),
      ...(typeof search['redirect'] === 'string' ? { redirect: search['redirect'] } : {}),
    }),
    beforeLoad: requireOrganizationSelection,
  })
  const organizationCreateRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/organizations/new',
    component: screens.organizationCreate,
    staticData: { title: 'organizations.createTitle' },
    beforeLoad: requireOrganizationSelection,
  })
  // Both account-only entries are workspaces standing beside the shell, so
  // this owner hands them the form factor the shell reads for its own
  // workspace; a page never asks the viewport for itself.
  const AccountConnectors = screens.connectors
  const accountConnectorsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/account/connectors',
    component: () => <FormFactorProvider><AccountConnectors /></FormFactorProvider>,
    staticData: { title: 'connectors.title' },
    beforeLoad: requireAccountConnectors,
  })
  const requiredPasswordChangeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/password-change',
    component: screens.passwordChange,
    staticData: { title: 'profile.passwordTitle' },
    validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
      typeof search['redirect'] === 'string' ? { redirect: search['redirect'] } : {},
    beforeLoad: requirePasswordChange,
  })
  const AccountIntegrations = screens.integrations
  const accountIntegrationsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/account/integrations',
    component: () => <FormFactorProvider><AccountIntegrations /></FormFactorProvider>,
    staticData: { title: 'externalAccess.title' },
    beforeLoad: requireOrganizationSelection,
  })
  const AccountDesignIntegrations = screens.designIntegrations
  const accountDesignIntegrationsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/account/design-integrations',
    component: () => <FormFactorProvider><AccountDesignIntegrations /></FormFactorProvider>,
    staticData: { title: 'designAccess.title' },
    validateSearch: externalAuthorizationSearch,
    beforeLoad: requireOrganizationSelection,
  })
  // Design access is the account's, so an account without a selected clinic
  // still reads its design drafts beside its design connections; a workspace
  // session reads them in the profile.
  const AccountDesignDrafts = screens.designDrafts
  const accountDesignDraftsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/account/design-drafts',
    component: () => <FormFactorProvider><AccountDesignDrafts /></FormFactorProvider>,
    staticData: { title: 'profile.nav.designDrafts' },
    beforeLoad: requireAccountDesignDrafts,
  })
  const AccountDesignDraft = screens.designDraft
  const accountDesignDraftRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/account/design-drafts/$proposalId',
    component: () => <FormFactorProvider><AccountDesignDraft /></FormFactorProvider>,
    staticData: { title: 'profile.nav.designDrafts' },
    validateSearch: designProposalSearch,
    beforeLoad: requireAccountDesignDrafts,
  })
  const publicPatientDocumentRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/documents/$token',
    component: screens.publicDocument,
    staticData: { title: 'patient.documents.public.title' },
  })
  const staffInvitationRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/staff-invitation',
    component: screens.invitation,
    staticData: { title: 'users.invitation.title' },
  })
  const authenticatedRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'authenticated',
    component: screens.shell,
    staticData: { title: null },
    errorComponent: ErrorPanel,
    beforeLoad: requireApplication,
  })
  return { rootRoute, authenticatedRoute, publicRoutes: [loginRoute, organizationSelectionRoute, organizationCreateRoute,
    accountConnectorsRoute, accountIntegrationsRoute, accountDesignIntegrationsRoute, accountDesignDraftsRoute, accountDesignDraftRoute, requiredPasswordChangeRoute, publicPatientDocumentRoute, staffInvitationRoute] as const }
}
